import type { FinishFileSpec } from '../types.js';

function isValidRange(range: unknown): range is [number, number] {
  return (
    Array.isArray(range) &&
    range.length >= 2 &&
    typeof range[0] === 'number' &&
    typeof range[1] === 'number' &&
    Number.isFinite(range[0]) &&
    Number.isFinite(range[1]) &&
    range[0] > 0 &&
    range[1] >= range[0]
  );
}

function extractValidRanges(lines: unknown): Array<[number, number]> | null {
  if (!Array.isArray(lines)) return null;
  const valid: Array<[number, number]> = [];
  for (const range of lines) {
    if (isValidRange(range)) {
      valid.push([range[0], range[1]]);
    }
  }
  return valid.length > 0 ? valid : null;
}

export function normalizeFinishFiles(files: FinishFileSpec[]): FinishFileSpec[] {
  return files.map((f) => {
    if (f.lines === '*') return { path: f.path, lines: '*' };
    const validRanges = extractValidRanges(f.lines);
    if (!validRanges) return { path: f.path, lines: '*' };
    return { path: f.path, lines: mergeRanges(validRanges) };
  });
}

export async function readFinishFiles(
  repoRoot: string,
  files: FinishFileSpec[],
  reader: (path: string, start?: number, end?: number) => Promise<string[]>
): Promise<{ path: string; ranges: '*' | Array<[number, number]>; content: string }[]> {
  const out: { path: string; ranges: '*' | Array<[number, number]>; content: string }[] = [];
  for (const f of files) {
    const validRanges = f.lines === '*' ? null : extractValidRanges(f.lines);
    if (f.lines === '*' || !validRanges) {
      const lines = await reader(f.path);
      out.push({ path: f.path, ranges: '*', content: lines.join('\n') });
    } else {
      const ranges = mergeRanges(validRanges);
      const chunks: string[] = [];
      for (let i = 0; i < ranges.length; i++) {
        const [s, e] = ranges[i];
        if (i === 0 && s > 1) {
          chunks.push(`// ... existing code, block starting at line ${s} ...`);
        } else if (i > 0) {
          chunks.push(`// ... existing code, block starting at line ${s} ...`);
        }
        const lines = await reader(f.path, s, e);
        chunks.push(lines.join('\n'));
      }
      out.push({ path: f.path, ranges, content: chunks.join('\n') });
    }
  }
  return out;
}

function mergeRanges(ranges: Array<[number, number]>): Array<[number, number]> {
  if (!ranges.length) return [];
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  let [cs, ce] = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const [s, e] = sorted[i];
    if (s <= ce + 1) {
      ce = Math.max(ce, e);
    } else {
      merged.push([cs, ce]);
      cs = s;
      ce = e;
    }
  }
  merged.push([cs, ce]);
  return merged;
}

